import { useCallback, useEffect, useRef, useState } from "react";
import SearchInput from "../components/Forms/SearchInput";
import DataTable from "../components/Tables/Datatable";
import { orderStatus, PERMISSION_IDS, SELECTED_CUSTOMER_STORAGE_KEY } from "../constant";
import { postApi } from "../services/api";
import { Link, useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { formatDate, handleRouteNav, numberFormatter } from "../utils";
import Loader from "../common/Loader";
import Filters from "../components/Filters";
import { useSelector } from "react-redux";
import usePagination from "../hooks/usePagination";
import { CustomDot } from "../components/Charts/CustomBadge";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { Breadcrumb, Select } from "antd";
import AgentClientSelect from "../components/AgentClientSelect";
import PrimaryButton from "../components/Forms/PrimaryButton";

const toAmount = (value) => {
  const numericValue = Number(String(value ?? 0).replace(/,/g, ""));
  return Number.isFinite(numericValue) ? numericValue : 0;
};

export default function Outstandings() {
  const { appliedFilters, ModalUI, DrawerUI, FilterButton, resetFilters, hasFilters } = Filters({
    filters: [
      {
        type: "daterange",
        key: "bill_date",
        label: "Bill Date",
      },
      // {
      //   type: "select",
      //   key: "total_out_standing_amount",
      //   label: "Total Outstanding Amount",
      //   options: [
      //     { label: "< 1000", value: "-1000" },
      //     { label: "1000 - 5000", value: "1000-5000" },
      //     { label: "5000 - 10000", value: "5000-10000" },
      //     { label: "> 10000", value: "10000-" },
      //   ],
      // },
      // {
      //   type: "select",
      //   key: "on_account_amount",
      //   label: "On Account Amount",
      //   options: [
      //     { label: "< 1000", value: "-1000" },
      //     { label: "1000 - 5000", value: "1000-5000" },
      //     { label: "5000 - 10000", value: "5000-10000" },
      //     { label: "> 10000", value: "10000-" },
      //   ],
      // },
      // {
      //   type: "select",
      //   key: "out_standing_amount",
      //   label: "Outstanding Amount",
      //   options: [
      //     { label: "< 1000", value: "-1000" },
      //     { label: "1000 - 5000", value: "1000-5000" },
      //     { label: "5000 - 10000", value: "5000-10000" },
      //     { label: "> 10000", value: "10000-" },
      //   ],
      // },
    ],
    onChange: () => resetPage(),
    persistToUrl: false,
  });

  const { is_sales_person, is_admin, is_sales_manager, user_id, is_customer, permission_ids, role_id, customer_id, company_name, is_agent } =
    useSelector((state) => state.auth);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [orderData, setOrderData] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [loading, setLoading] = useState(false);
  const { pagination, setPagination, handleChange: handlePaginationChange, resetPage } = usePagination(10, { persist: false });
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const [customerListLoading, setCustomerListLoading] = useState(false);
  const [allCustomers, setAllCustomers] = useState([{ customer_id, company_name }]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(() => {
    const stored = sessionStorage.getItem(SELECTED_CUSTOMER_STORAGE_KEY);
    if (stored === null || stored === "") return customer_id;
    return Number.isNaN(Number(stored)) ? stored : Number(stored);
  });
  const customerSelectRef = useRef(null);

  const location = useLocation();
  const { selectedClient } = location.state || {};

  const toggleExpand = (key) => {
    setExpandedRowKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const noOfRowsToShow = 2;
  const showFirstOnly = false;

  const getVisibleBills = (bills, isExpanded) => {
    if (!bills || bills.length <= (showFirstOnly ? noOfRowsToShow : noOfRowsToShow * 2)) return bills;
    if (isExpanded) return bills;

    return showFirstOnly
      ? [...bills.slice(0, noOfRowsToShow), { isEllipsis: true, _key: "ellipsis" }]
      : [...bills.slice(0, noOfRowsToShow), { isEllipsis: true, _key: "ellipsis" }, ...bills.slice(-noOfRowsToShow)];
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const fetchIdRef = useRef(0);

  useEffect(() => {
    if (!is_agent) return;

    const fetchCustomersForAgent = async () => {
      setCustomerListLoading(true);
      try {
        const response = await postApi("sales/agent/agent-customer-list", {
          agent_customer_id: customer_id,
        });

        const customers = response?.data || [];
        const hasSelf = customers.some((item) => String(item.customer_id) === String(customer_id));

        const normalizedCustomers = hasSelf
          ? customers
          : [
              {
                customer_id,
                company_name: company_name || "Self",
              },
              ...customers,
            ];

        setAllCustomers(normalizedCustomers);

        const stored = sessionStorage.getItem(SELECTED_CUSTOMER_STORAGE_KEY);
        const normalizedStored = stored === null || stored === "" ? null : Number.isNaN(Number(stored)) ? stored : Number(stored);

        const hasStoredInList = normalizedCustomers.some((item) => String(item.customer_id) === String(normalizedStored));

        const nextCustomerId = hasStoredInList ? normalizedStored : customer_id;

        setSelectedCustomerId(nextCustomerId);
        sessionStorage.setItem(SELECTED_CUSTOMER_STORAGE_KEY, String(nextCustomerId));
      } finally {
        setCustomerListLoading(false);
      }
    };

    fetchCustomersForAgent();
  }, [is_agent, customer_id, company_name]);

  const resetForCustomerChange = () => {
    resetFilters();
    setSearchParams(
      (prev) => {
        prev.delete("q");
        return prev;
      },
      { replace: true },
    );
    setPagination((prev) => ({ ...prev, current: 1 }));
    setOrderData([]);
    setTotalOrders(0);
    setExpandedRowKeys([]);
  };

  const fetchOrders = useCallback(async () => {
    fetchIdRef.current += 1;
    const currentFetchId = fetchIdRef.current;
    try {
      setLoading(true);
      const { bill_date, total_out_standing_amount, on_account_amount, out_standing_amount } = appliedFilters;

      const filters = {
        total_out_standing_amount,
        on_account_amount,
        out_standing_amount,
        from_date: bill_date?.[0] ? formatDate(new Date(bill_date[0]), { format: "YYYY-MM-DD" }) : undefined,
        to_date: bill_date?.[1] ? formatDate(new Date(bill_date[1]), { format: "YYYY-MM-DD" }) : undefined,
      };

      const response = await postApi(
        is_customer ? "credit/get-customer-outstanding-parties" : "credit/get-sales-outstanding-parties",
        {
          search: query,
          page: pagination.current,
          per_page: pagination.pageSize,
          ...(Object.keys(appliedFilters).length ? filters : {}),
          ...(is_sales_person && !permission_ids.includes(PERMISSION_IDS.VIEW_ALL_OUTSTANDING) ? { user_id } : {}),
          ...(is_customer ? { customer_id: is_agent === 1 ? selectedCustomerId || customer_id : selectedClient || customer_id } : {}),
        },
        null,
      );

      if (currentFetchId !== fetchIdRef.current) {
        return;
      }

      const formattedRows = (response?.data || []).map((party, index) => {
        const bills = (party.billing_data || []).map((b, i) => ({
          ...b,
          _key: b.out_standing_id || i,
          billNo: b.bill_no || "-",
          billDate: b.bill_date || null,
          outstanding: toAmount(b.out_standing_amt),
        }));

        const totalOutstanding = bills.reduce((sum, bill) => sum + bill.outstanding, 0);
        const totalOnAccount = toAmount(party.on_account?.on_account_amt);
        const totalNet = totalOnAccount - totalOutstanding;

        return {
          ...party,
          key: party.party_code || index,
          partyCode: party.party_code || "-",
          partyName: party.partyname || "-",
          bills,
          totalOutstanding,
          totalOnAccount,
          totalNet,
        };
      });

      setOrderData(formattedRows);
      setTotalOrders(response?.total || 0);
    } catch (error) {
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }
      console.error("Error fetching orders:", error);
    } finally {
      setExpandedRowKeys([]);
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }
      setLoading(false);
    }
  }, [
    appliedFilters,
    is_sales_person,
    pagination.current,
    pagination.pageSize,
    query,
    user_id,
    is_customer,
    is_agent,
    selectedCustomerId,
    selectedClient,
    customer_id,
  ]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchOrders();
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [fetchOrders]);

  const showAssignedSales = is_sales_manager || is_admin || (is_sales_person && permission_ids.includes(PERMISSION_IDS.VIEW_ALL_OUTSTANDING));

  const columns = [
    {
      title: "Client",
      key: "party",
      dataIndex: "company_name",
      width: 220,
      ellipsis: true,
      onCell: () => ({ style: { verticalAlign: "top" } }),
      render: (_, record) => {
        return (
          <div className="flex flex-col gap-1 text-left">
            <div className="flex gap-1 flex-col text-start">
              <span className="font-semibold text-slate-900 truncate" title={record.company_name || "-"}>
                {record.company_name || "-"}
              </span>
              {showAssignedSales && (
                <span className="text-sm text-slate-500 truncate" title={record.assigned_sale_person || "-"}>
                  {record.assigned_sale_person || "-"}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Bill No.",
      key: "billNo",
      onCell: () => ({ style: { verticalAlign: "top" } }),
      render: (_, record) => {
        const isExpanded = expandedRowKeys.includes(record.key);
        const visibleBills = getVisibleBills(record.bills, isExpanded);

        return (
          <div className="flex flex-col gap-1">
            {visibleBills.length === 0 ? (
              <div className="text-slate-500">-</div>
            ) : (
              visibleBills.map((bill) => {
                if (bill.isEllipsis)
                  return (
                    <div key={`ell-${record.key}`} className="text-slate-400 font-bold tracking-widest leading-relaxed">
                      ...
                    </div>
                  );
                return (
                  <div key={bill._key} className="text-slate-900 truncate">
                    {bill.billNo || "-"}
                  </div>
                );
              })
            )}
          </div>
        );
      },
    },
    {
      title: "Bill Date",
      key: "billDate",
      onCell: () => ({ style: { verticalAlign: "top" } }),
      render: (_, record) => {
        const isExpanded = expandedRowKeys.includes(record.key);
        const visibleBills = getVisibleBills(record.bills, isExpanded);

        return (
          <div className="flex flex-col gap-1">
            {visibleBills.length === 0 ? (
              <div className="text-slate-500">-</div>
            ) : (
              visibleBills.map((bill) => {
                if (bill.isEllipsis)
                  return (
                    <div key={`ell-${record.key}`} className="text-slate-400 font-bold tracking-widest leading-relaxed">
                      ...
                    </div>
                  );
                return (
                  <div key={bill._key} className="text-slate-500 truncate">
                    {bill.billDate ? formatDate(bill.billDate) : "-"}
                  </div>
                );
              })
            )}

            <div className="font-bold text-slate-900 mt-3 border-t border-slate-200 pt-2 uppercase">TOTAL</div>
            {record.bill_no_count === record?.billing_data?.length && (
              <>
                <div className="text-slate-500 mt-1 font-medium">On account</div>
                <div className="font-bold text-slate-900 mt-1 uppercase flex items-center gap-1">
                  <CustomDot color={record.totalNet < 0 ? "#FF0000" : "#009F10"} /> NET
                </div>
              </>
            )}
          </div>
        );
      },
    },
    {
      title: "Amount",
      key: "billAmount",
      onCell: () => ({ style: { verticalAlign: "top" } }),
      render: (_, record) => {
        const isExpanded = expandedRowKeys.includes(record.key);
        const visibleBills = getVisibleBills(record.bills, isExpanded);

        return (
          <div className="flex flex-col gap-1">
            {visibleBills.length === 0 ? (
              <div className="text-slate-500">-</div>
            ) : (
              visibleBills.map((bill) => {
                if (bill.isEllipsis)
                  return (
                    <div key={`ell-${record.key}`} className="text-slate-400 font-bold tracking-widest leading-relaxed">
                      ...
                    </div>
                  );
                return (
                  <div key={bill._key} className="text-slate-900 font-medium truncate">
                    {numberFormatter(bill.outstanding)}
                  </div>
                );
              })
            )}

            <div className="font-bold text-slate-900 mt-3 border-t border-slate-200 pt-2">{numberFormatter(record.totalOutstanding)}</div>
            {record.bill_no_count === record?.billing_data?.length && (
              <>
                <div className="text-slate-900 font-medium mt-1">{numberFormatter(record.totalOnAccount)}</div>
                <div className={`font-bold ${record.totalNet < 0 ? "text-[#FF0000]" : "text-[#009F10]"} mt-1`}>
                  {numberFormatter(record.totalNet)}
                </div>
              </>
            )}
          </div>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 50,
      fixed: "right",
      onCell: () => ({ style: { verticalAlign: "top" } }),
      render: (_, record) => {
        {
          const billsLength = (record.bills || []).length;
          const isExpanded = expandedRowKeys.includes(record.key);

          return billsLength > (showFirstOnly ? noOfRowsToShow : noOfRowsToShow * 2) ? (
            <div
              onClick={() => toggleExpand(record.key)}
              className="flex items-center hover:underline underline-offset-2 gap-1 w-[110px] cursor-pointer text-[#A33434] transition-opacity hover:opacity-80"
            >
              <span className="text-[13px] font-medium ">View {isExpanded ? "Less" : "More"}</span>
              {isExpanded ? <FaChevronUp className="text-[12px]" /> : <FaChevronDown className="text-[12px]" />}
            </div>
          ) : null;
        }
      },
    },
  ].filter(Boolean);

  const handleChange = (paginationInfo, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    handlePaginationChange(paginationInfo, filters, sorter);
  };

  const onSearch = (value, _e, info) => console.log(info?.source, value);

  const handleOnSearchChange = (e) => {
    const value = e.target.value;
    setSearchParams(
      (prev) => {
        if (value) prev.set("q", value);
        else prev.delete("q");
        return prev;
      },
      { replace: true },
    );
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleRow = (record) => {};

  const { setTitle, setDescription, setBackButton } = useOutletContext();

  useEffect(() => {
    setTitle("Outstanding");
    setBackButton(true);
    setDescription(
      <div>
        <Breadcrumb>
          {[
            { title: "Home", href: handleRouteNav("", role_id) },
            {
              title: is_customer ? "My Orders" : "Orders",
              href: handleRouteNav("/orders", role_id),
            },
            {
              title: "Outstanding",
              href: handleRouteNav(`/orders/outstanding`, role_id),
            },
          ].map((item, index) => (
            <Breadcrumb.Item key={index}>
              <Link to={item.href}>{item.title}</Link>
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
      </div>,
    );
  }, [setTitle, setDescription, setBackButton, totalOrders]);

  return (
    <>
      <ModalUI />
      <div className="bg-[#FFFFFF]" style={{ borderRadius: "32px", overflow: "hidden" }}>
        <div className="flex justify-between gap-7 items-center px-8 py-6">
          {/* <AgentClientSelect
            selectedCustomerId={selectedCustomerId}
            onClientChange={(newId) => {
              setSelectedCustomerId(newId);
              resetForCustomerChange();
            }}
          /> */}
          <div className="flex items-center gap-3 w-full">
            <SearchInput onSearch={onSearch} value={query} onChange={handleOnSearchChange} placeholder="Search for bill nos." />
          </div>
          <div className="flex gap-3 items-center">
            <FilterButton />
            {hasFilters && is_customer && (
              <PrimaryButton btnType="secondary" text="Remove" onClick={() => resetFilters()} textClassName="" className="!w-[130px]" />
            )}
          </div>
        </div>
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader size={60} />
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={orderData}
            handleChange={handleChange}
            handleRow={handleRow}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: totalOrders,
              showSizeChanger: true,
            }}
            className={`no-row-hover ${is_customer ? "pb-6" : ""}`}
            Showpagination={!is_customer}
          />
        )}
      </div>
    </>
  );
}
